import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { DashList, Formula, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.que-food-cost-deberia-tener-restaurante-espana.${field}`

export function FoodCostEspanaArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'creencia', label: t(k('toc.1'), '1. No hay un % correcto para todos') },
        { id: 'marco', label: t(k('toc.2'), '2. Qué es el food cost y sobre qué se calcula') },
        { id: 'modelo', label: t(k('toc.3'), '3. Qué mueve el techo en tu modelo') },
        { id: 'prime-cost', label: t(k('toc.4'), '4. El freno del prime cost') },
        { id: 'compras', label: t(k('toc.5'), '5. Si calculas con compras, el número miente') },
        { id: 'comprobaciones', label: t(k('toc.6'), '6. Tres comprobaciones si tu % te asusta') },
        { id: 'semana', label: t(k('toc.7'), '7. Tres números que miraría antes del viernes') },
        { id: 'matices', label: t(k('toc.8'), '8. Cuándo un 35 % es sano y un 25 % es mentira') },
    ]

    const weekItems = [
        t(
            k('week.1'),
            'Saca el food cost del mes pasado con inventario inicial y final, no solo con la suma de facturas. Anota el %.',
        ),
        t(
            k('week.2'),
            'Pon al lado el coste de personal del mismo mes y suma ambos: ese prime cost es el freno, no el food cost solo.',
        ),
        t(
            k('week.3'),
            'Elige un plato ancla de la carta, actualiza su escandallo con el precio de compra de esta semana y mira el margen en euros, no solo el %.',
        ),
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'No hay un food cost mágico. La referencia habitual va del 25 % al 35 % sobre ventas netas, pero el % bueno es el que aguanta tu modelo junto al personal. Si calculas solo con compras, el número miente.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(k('heroAlt'), 'Pase de cocina con varios platos listos para salir')}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd, 'text-article-muted-soft')}>
                                {t(k('updated'), 'Actualizado el 13 de septiembre de 2026.')}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.1'),
                                    'Estás con el PyG abierto o con el escandallo a medias y la pregunta te sale sola: «¿Mi food cost debería estar en el 25 %, en el 30 % o puedo llegar al 35 %?»',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.2'),
                                    'La respuesta cómoda sería un número. La útil es otra: el food cost correcto es el que tu modelo puede soportar y seguir siendo rentable cuando sumas personal, alquiler y el resto.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                <span className={articleUi.em}>
                                    {t(
                                        k('intro.em'),
                                        'No existe un porcentaje perfecto que sirva para todos los restaurantes.',
                                    )}
                                </span>
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='creencia' className={articleUi.h2}>
                        {t(k('h2.creencia'), 'No hay un % correcto para todos')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.creencia.1'),
                                'Se busca una cifra porque es fácil de comparar y de repetir en WhatsApp. Una cafetería, una hamburguesería, una marisquería y un restaurante gastronómico no tienen la misma estructura de costes.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.creencia.2'),
                                'El tipo de producto, la elaboración, el ticket medio, el peso de las bebidas y el personal cambian el resultado. Perseguir el 28 % «porque lo dijo alguien» sin mirar el resto es cómo te comes el margen sin verlo.',
                            )}
                        </p>
                    </div>

                    <h2 id='marco' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.marco'), 'Qué es el food cost y sobre qué se calcula')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.marco.1Before'),
                                'El food cost es el porcentaje de las ventas netas que se va en el coste del producto vendido: alimentos, bebidas, ingredientes, envases y consumibles de venta. Ventas y costes, sin IVA. Si quieres la definición completa (teórico vs real, plato vs negocio), empieza por ',
                            )}
                            <LearnArticleLink slug='que-es-el-food-cost'>
                                {t(k('linkQueEs'), 'qué es el food cost')}
                            </LearnArticleLink>
                            {t(k('p.marco.1After'), '.')}
                        </p>
                        <Formula variant='tight'>
                            {t(k('formula.foodCost'), 'Food cost = coste de producto ÷ ventas netas × 100')}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.marco.2'), 'Como orientación, muchos negocios de restauración trabajan entre el ')}
                            <strong>{t(k('p.marco.2Strong'), '25 % y el 35 %')}</strong>
                            {t(
                                k('p.marco.2After'),
                                ' de las ventas netas. Es brújula, no norma. Si aún no tienes coste por plato, el camino es el ',
                            )}
                            <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                {t(k('linkEscandallo'), 'escandallo con precios de esta semana')}
                            </LearnArticleLink>
                            {t(k('p.marco.2End'), ', no el promedio del sector.')}
                        </p>
                    </div>

                    <div className={articleUi.stats}>
                        <div className={articleUi.stat}>
                            <p className={articleUi.statLabel}>{t(k('stat.netSales'), 'Ventas netas')}</p>
                            <p className={articleUi.statValueSm}>{t(k('stat.netSalesValue'), '50.000 €')}</p>
                        </div>
                        <div className={articleUi.stat}>
                            <p className={articleUi.statLabel}>{t(k('stat.productCost'), 'Coste de producto')}</p>
                            <p className={articleUi.statValueSm}>{t(k('stat.productCostValue'), '15.000 €')}</p>
                        </div>
                        <div className={articleUi.statAccent}>
                            <p className={articleUi.statLabelAccent}>{t(k('stat.foodCost'), 'Food cost')}</p>
                            <p className={cn(articleUi.statValueSm, 'text-article-ember')}>
                                {t(k('stat.foodCostValue'), '30 %')}
                            </p>
                        </div>
                    </div>

                    <div className={articleUi.prose}>
                        <Formula variant='tight'>{t(k('formula.ejemplo'), '15.000 € ÷ 50.000 € × 100 = 30 %')}</Formula>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.marco.3'),
                                'Por cada 100 € vendidos, 30 € se van en producto. Los otros 70 € no son beneficio: aún tienen que pagar personal, alquiler, suministros, gestoría, comisiones y el resto.',
                            )}
                        </p>
                    </div>

                    <h2 id='modelo' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.modelo'), 'Qué mueve el techo en tu modelo')}
                    </h2>
                    <div className={articleUi.prose} style={{ marginBottom: 24 }}>
                        <p className={articleUi.p} style={{ marginBottom: 0 }}>
                            {t(
                                k('p.modelo.lead'),
                                'Antes de enamorarte de un %, mira qué tipo de negocio eres. Estas bandas son orientación, no contrato.',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.compare}>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabel}>{t(k('compare.low.label'), '25 % – 30 %')}</p>
                            <p className={articleUi.compareTitle}>{t(k('compare.low.title'), 'Producto controlado')}</p>
                            <p className={articleUi.compareBody}>
                                {t(
                                    k('compare.low.body'),
                                    'Buena rotación, procesos relativamente sencillos, menos materia prima cara.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabel}>{t(k('compare.mid.label'), '30 % – 35 %')}</p>
                            <p className={articleUi.compareTitle}>{t(k('compare.mid.title'), 'Más complejidad')}</p>
                            <p className={articleUi.compareBody}>
                                {t(
                                    k('compare.mid.body'),
                                    'Materias primas más caras, elaboraciones largas o más peso gastronómico.',
                                )}
                            </p>
                        </div>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabelAlert}>
                                {t(k('compare.high.label'), 'Por encima del 35 %')}
                            </p>
                            <p className={articleUi.compareTitle}>
                                {t(k('compare.high.title'), 'Revisa antes de asumir')}
                            </p>
                            <p className={articleUi.compareBody}>
                                {t(
                                    k('compare.high.body'),
                                    'Puede ser el modelo (marisco, madurados) o un fallo de compras, porciones o inventario.',
                                )}
                            </p>
                        </div>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.modelo.1'),
                                'Las bebidas suelen llevar otro % que la cocina: conviene mirar comida, bebida y total por separado. Y el precio de compra no es el coste usable: si el pescado te sale a ',
                            )}
                            <strong>{t(k('p.modelo.1Strong'), '12 €/kg')}</strong>
                            {t(k('p.modelo.1Mid'), ' y solo aprovechas el ')}
                            <strong>{t(k('p.modelo.1Strong2'), '60 %')}</strong>
                            {t(k('p.modelo.1End'), ', el coste real es 20 €/kg aprovechable.')}
                        </p>
                        <Formula variant='tight'>
                            {t(k('formula.yieldExample'), '12 € ÷ 60 % = 20 €/kg utilizable')}
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.modelo.2'),
                                'Sin rendimientos, el escandallo miente a la baja. El food cost mide producto; no mide solo las horas de cocina que ese producto exige.',
                            )}
                        </p>
                    </div>

                    <h2 id='prime-cost' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.primeCost'), 'El freno del prime cost')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.primeCost.1'),
                                'El % de producto no decide solo. Prime cost = coste de producto + coste de personal. Dos locales con ',
                            )}
                            <strong>{t(k('p.primeCost.1Strong'), '100.000 €')}</strong>
                            {t(k('p.primeCost.1After'), ' de ventas netas:')}
                        </p>
                    </div>

                    <div className={articleUi.compare}>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabel}>{t(k('compare.a.label'), 'Local A')}</p>
                            <p className={articleUi.compareTitle}>{t(k('compare.a.title'), 'Food cost más bajo')}</p>
                            <p className={articleUi.compareBody}>
                                {t(k('compare.a.body'), 'Producto 26.000 € · Personal 38.000 € · Prime cost 64.000 €')}
                            </p>
                        </div>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabel}>{t(k('compare.b.label'), 'Local B')}</p>
                            <p className={articleUi.compareTitle}>
                                {t(k('compare.b.title'), 'Más margen tras producto + personal')}
                            </p>
                            <p className={articleUi.compareBody}>
                                {t(k('compare.b.body'), 'Producto 33.000 € · Personal 24.000 € · Prime cost 57.000 €')}
                            </p>
                        </div>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.primeCost.2'),
                                'A tiene mejor food cost. B conserva más margen cuando sumas personal. Bajar producto a costa de más horas de cocina no siempre mejora el resultado. El equilibrio es producto + personal + ventas, no el % más bajo de la carta.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.primeCost.3Before'),
                                'El 65 %, el 70 %, el sueldo del dueño y el techo según el alquiler los desmontamos en ',
                            )}
                            <LearnArticleLink slug='que-es-el-prime-cost'>
                                {t(k('p.primeCost.3Link'), 'qué es el prime cost de un restaurante')}
                            </LearnArticleLink>
                            {t(k('p.primeCost.3After'), '.')}
                        </p>
                    </div>

                    <section id='compras' className={articleUi.dark}>
                        <h2 className={articleUi.h2}>
                            {t(k('h2.compras'), 'Si calculas con compras, el número miente')}
                        </h2>
                        <div className={articleUi.prose}>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('p.compras.1'),
                                    'Esta es la pega que más duele: usar solo las compras del mes como si fueran el coste consumido. Sirve de aproximación y también te engaña.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(k('p.compras.2'), 'Ventas netas ')}
                                <strong>{t(k('p.compras.2Strong'), '40.000 €')}</strong>
                                {t(k('p.compras.2Mid'), '. Compras ')}
                                <strong>{t(k('p.compras.2Strong2'), '16.000 €')}</strong>
                                {t(k('p.compras.2End'), '. Solo con compras:')}
                            </p>
                            <Formula variant='dark'>
                                {t(k('formula.comprasWrong'), '16.000 € ÷ 40.000 € × 100 = 40 %')}
                            </Formula>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('p.compras.3'),
                                    'Parece un desastre. Inventario inicial 8.000 €, compras 16.000 €, inventario final 12.000 €:',
                                )}
                            </p>
                            <Formula variant='dark'>
                                {t(k('formula.inventoryLine2Before'), '8.000 € + 16.000 € − 12.000 € = ')}
                                <strong>{t(k('formula.inventoryLine2Strong'), '12.000 €')}</strong>
                                <br />
                                {t(k('formula.comprasRightBefore'), '12.000 € ÷ 40.000 € × 100 = ')}
                                <strong>{t(k('formula.comprasRightStrong'), '30 %')}</strong>
                            </Formula>
                            <p className={articleUi.pMuted} style={{ marginBottom: 0 }}>
                                {t(k('p.compras.4'), 'No tenías un 40 %: tenías stock. Es la misma lógica que ')}
                                <LearnArticleLink slug='coste-teorico-vs-coste-real-cocina'>
                                    {t(k('linkTeoricoReal'), 'coste teórico frente a coste real')}
                                </LearnArticleLink>
                                {t(k('p.compras.4End'), '.')}
                            </p>
                        </div>
                    </section>

                    <h2 id='comprobaciones' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.comprobaciones'), 'Tres comprobaciones si tu % te asusta')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.comprobaciones.lead'),
                                'Antes de cambiar proveedor o recortar raciones, en este orden:',
                            )}
                        </p>
                        <DashList
                            items={[
                                t(
                                    k('list.check.1'),
                                    'Inventario: ¿el % sale de consumo real o de una compra gorda de vino y secos?',
                                ),
                                t(
                                    k('list.check.2'),
                                    'Escandallos y gramajes: ¿faltan aceites, salsas y guarnición? ¿la cocina respeta el peso?',
                                ),
                                t(
                                    k('list.check.3'),
                                    'Prime cost y margen en euros: un plato al 32 % puede dejarte más euros que uno al 20 % si el ticket es otro.',
                                ),
                            ]}
                        />
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.comprobaciones.dish'), 'Ejemplo de margen, no de religión del %:')}
                        </p>
                    </div>

                    <div className={articleUi.compare}>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabel}>{t(k('compare.dishA.label'), 'Plato A')}</p>
                            <p className={articleUi.compareTitle}>{t(k('compare.dishA.title'), 'Food cost 20 %')}</p>
                            <p className={articleUi.compareBody}>
                                {t(k('compare.dishA.body'), 'Precio sin IVA 10 € · Coste 2 € · Margen bruto 8 €')}
                            </p>
                        </div>
                        <div className={articleUi.compareCard}>
                            <p className={articleUi.compareLabel}>{t(k('compare.dishB.label'), 'Plato B')}</p>
                            <p className={articleUi.compareTitle}>{t(k('compare.dishB.title'), 'Food cost 32 %')}</p>
                            <p className={articleUi.compareBody}>
                                {t(k('compare.dishB.body'), 'Precio sin IVA 25 € · Coste 8 € · Margen bruto 17 €')}
                            </p>
                        </div>
                    </div>

                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.comprobaciones.2Before'),
                                'B deja 9 € más por venta. El % importa; el margen en euros, la rotación y la dificultad también. Para ordenar la carta, ',
                            )}
                            <LearnArticleLink slug='disenar-carta-rentable-restaurante'>
                                {t(k('linkCarta'), 'empieza por los platos que más mueven dinero')}
                            </LearnArticleLink>
                            {t(k('p.comprobaciones.2After'), '.')}
                        </p>
                    </div>

                    <h2 id='semana' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.semana'), 'Tres números que miraría antes del viernes')}
                    </h2>
                    <ol className={articleUi.numList}>
                        {weekItems.map(item => (
                            <li key={item} className={articleUi.numItem}>
                                {item}
                            </li>
                        ))}
                    </ol>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.semana.note'),
                                'Si no tienes claro food cost frente a margen, el desglose está en ',
                            )}
                            <LearnArticleLink slug='food-cost-y-margen-bruto-restaurante'>
                                {t(k('linkMargen'), 'food cost y margen bruto en euros')}
                            </LearnArticleLink>
                            {t(k('p.semana.noteEnd'), '.')}
                        </p>
                    </div>

                    <h2 id='matices' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.matices'), 'Cuándo un 35 % es sano y un 25 % es mentira')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.1'),
                                'Un 35 % puede ser sano en marisco o producto caro con ticket alto, poca merma mal controlada y personal contenido. Un 25 % puede ser mentira si el inventario no se valora, si las porciones se van o si el personal se come lo que el producto «ahorra».',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.2'),
                                'No hay un food cost oficial único publicado para todos los modelos en España. La banda 25–35 % es orientación de sector, no un dato de BOE ni del INE. Tu número bueno sale de tu mix, tu prime cost y tu caja, no de un ranking de WhatsApp.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.matices.3Before'), 'Desde ')}
                            <TipiLink />
                            {t(
                                k('p.matices.3After'),
                                ' te ayudamos a ver de dónde sale el %: compras, escandallos y desviaciones. No inventamos el food cost ideal de tu concepto; ponemos números a lo que ya cocinas.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.matices.4'),
                                'Si llevas meses mirando un % sin saber si es consumo real o ruido de almacén, cuéntanoslo y le ponemos números.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(k('closing.signoff'), 'Seguimos contando lo que a otros no les interesa contar.')}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Tienes un % y no sabes si puedes fiarte?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Con Tipi ves el coste de producto plato a plato y cuándo una subida o una desviación se come el margen. Sin prometerte un food cost mágico.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        <li>
                            {t(
                                k('sources.1'),
                                'La banda del 25 % al 35 % sobre ventas netas se usa aquí como orientación de sector en restauración, no como cifra oficial de una única fuente primaria española. No existe un food cost normativo único publicado por el INE o por Hostelería de España para todos los modelos.',
                            )}
                        </li>
                        <li>
                            {t(
                                k('sources.2'),
                                'Los ejemplos numéricos del artículo (mes a 50.000 €, inventarios, locales A/B, platos A/B y rendimiento del pescado) son ilustraciones propias para mostrar el método. No son datos de mercado de un local concreto.',
                            )}
                        </li>
                    </ul>
                </section>
            </article>
        </div>
    )
}
